import initialPageState from 'components/Pagelayout/initialPageState';
import { combineReducers, createReducer, ReducerOptionalMapObject } from 'store/createReducer';
import { ProductPageModel } from 'models/ProductPageModel';
import { IWidget, ListViewProduct, WidgetType } from 'models/ListViewProduct';
import { BhImageType } from 'models/BhImageType';
import {
  ADDON_SERVICE_DESELECT,
  ADDON_SERVICE_DESELECT_ALL,
  ADDON_SERVICE_SELECT,
  ADDON_SERVICE_ZIP_CODE_SET,
  AddonServiceSelectAction,
  AddonServiceZipCodeSetAction,
  BIG_MEDIA_ACTIVE,
  BIG_MEDIA_INACTIVE,
  BUNDLED_PRODUCT_SELECT,
  PRODUCT_PAGE_ADDITIONAL_DATA_SET,
  PRODUCT_PAGE_PRODUCT_ADDED_TO_CART,
  PRODUCT_PAGE_VARIANT_DATA_SET,
  PRODUCT_PAGE_VARIANT_LOADING_STATUS,
  ProductPageAdditionalDataAction,
  ProductPageBundledProductAction,
  ProductPageVariantDataAction,
  ProductPageVariantLoadingStatusAction
} from './actions';
import { isTopSellers } from 'views/ProductPage/selector';
import { ProductReview } from 'models/ProductReview';
import { addons } from './addons/reducer';
import { RelatedListViewProduct } from 'models/RelatedListViewProduct';
import { configurator } from 'views/ProductPage/configurators/reducer';
import { CONFIGURATOR_RESET } from 'views/ProductPage/configurators/actions';
import { RelatedProduct } from 'models/RelatedProduct';
import { PackageProduct } from 'models/PackageProduct';
import { Nullable } from './configurators/selector';
import { SampleProduct } from 'models/SampleProduct';
import { reviewReducers } from './reviews/themes/BGH/reducer';
import video, { initialState as videoInitialState } from 'components/VideoPlayer/reducer';
import { deselectAllProducts, deselectProductById, selectProductById } from './addonServices/utils';
import { UPDATE_CURRENT_PAGE } from 'components/Pagelayout/actions';
import { AddonServices } from 'models/AddonServices';
import { LoadingStatus } from '../../models/LoadingStatus';
import { IProductPageExtendedModel } from 'models/IProductPageExtendedModel';
import { BundleProductLink } from 'models/BundleProductLink';

const initialState: ProductPageModel = {
  ...initialPageState,
  addons: {},
  addonServices: undefined,
  accessories: [],
  reviews: [],
  othersAlsoBoughtProducts: [],
  bundledProduct: undefined,
  showProductAdded: false,
  samples: [],
  bundleProductLinks: [],
  breadCrumbsStructured: [],
  product: {
    variantName: '',
    shortDescription: {},
    brandName: '',
    attributes: [],
    summaryAttributes: [],
    description: {},
    productPdfs: [],
    colours: [],
    isFamily: false,
    mainWebCatalog: '',
    hasColourChoice: false,
    productId: '',
    variantId: '',
    url: '',
    title: '',
    titleSplitPos: 0,
    ticket: '',
    productImage: { type: BhImageType.Product, id: '', name: '' },
    brandLogo: { type: BhImageType.Product, id: '', name: '' },
    series: '',
    splashTextTop: '',
    splashTextMiddle: '',
    splashTextBottom: '',
    splashTextEnd: '',
    articleGroup: '',
    isCampaign: false,
    isPartialCampaign: false,
    isBundleSavings: false,
    isLowPrice: false,
    isLoweredPrice: false,
    isNewProduct: false,
    reviewAvgScore: 0,
    widgets: [],
    usps: [],
    isTopTested: false,
    brandUrl: '',
    isAddonProduct: false,
    meta: {
      deliveryEstimate: 0,
      stockStatus: 0,
      saleStatus: 0,
      showDeliveryInfo: false,
      publishedSaleDate: '',
      supplierProductNumber: '',
      stock: '',
      isInStock: false,
      isLowInStock: false,
      isBackOrder: false,
      showServiceChargeInfo: false
    },
    quantity: {
      amount: 1,
      unit: '',
      highest: 1,
      lowest: 1,
      salesQuantity: 0
    },
    price: {
      listPrice: 0,
      presentationPrice: 0,
      isFromPrice: false,
      currencyUnit: '',
      hiddenPrice: false,
      lowestPartPaymentPrice: 0
    },
    saleOrderCount: 0,
    energyLabelVariant: '',
    colorOptions: { nbrOfColors: 0, colors: [] },
    relevanceWithinCategory: '',
    isPartPrice: false,
    partPriceUnit: '',
    tacticalFlagMarking: '',
    tacticalFlagColor: '',
    isEligableForPartPayment: false,
    isThecaProduct: false,
    partPayments: []
  },
  // null to keep the key in the state
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  enticement: null!,
  isBot: false,
  video: videoInitialState,
  variantLoadingStatus: LoadingStatus.Idle,
  shouldUseNotifyMe: false,
  priceGuarantee: undefined
};

const setMediaWidgetActiveState = (isActive: boolean) => (widget: IWidget) =>
  widget.type !== WidgetType.Media ? widget : { ...widget, isActive };

const product = createReducer(initialState.product, {
  [PRODUCT_PAGE_ADDITIONAL_DATA_SET]: (
    state,
    { data: { product, efficiencyLabel } }: ProductPageAdditionalDataAction
  ) => ({
    ...state,
    ...product,
    efficiencyLabel: efficiencyLabel || state.efficiencyLabel
  }),
  [CONFIGURATOR_RESET]: state => ({
    ...state,
    variantId: '',
    isFamily: true
  }),
  [PRODUCT_PAGE_VARIANT_DATA_SET]: (
    state,
    { data: { product } }: ProductPageVariantDataAction
  ) => ({
    ...state,
    ...product
  }),
  [BIG_MEDIA_ACTIVE]: state => ({
    ...state,
    widgets: state.widgets?.map(setMediaWidgetActiveState(true))
  }),
  [BIG_MEDIA_INACTIVE]: state => ({
    ...state,
    widgets: state.widgets?.map(setMediaWidgetActiveState(false))
  })
});

const bundledProduct = createReducer(
  {},
  {
    [PRODUCT_PAGE_ADDITIONAL_DATA_SET]: (state, { data }: ProductPageAdditionalDataAction) =>
      data.bundledProduct || state,
    [BUNDLED_PRODUCT_SELECT]: (state, { selected }: ProductPageBundledProductAction) => ({
      ...state,
      selected
    })
  }
);

const topSellers = createReducer(
  {},
  {
    [PRODUCT_PAGE_ADDITIONAL_DATA_SET]: (
      state,
      { data: { topSellers } }: ProductPageAdditionalDataAction
    ) => (isTopSellers(topSellers) ? topSellers : state)
  }
);

const othersAlsoBoughtProducts = createReducer<RelatedListViewProduct[]>([], {
  [PRODUCT_PAGE_ADDITIONAL_DATA_SET]: (_, action: ProductPageAdditionalDataAction) =>
    action.data.othersAlsoBoughtProducts || [],
  [PRODUCT_PAGE_VARIANT_DATA_SET]: (_, action: ProductPageVariantDataAction) =>
    action.data.othersAlsoBoughtProducts || []
});
const reviews = createReducer<ProductReview[]>([], {
  [PRODUCT_PAGE_ADDITIONAL_DATA_SET]: (state, action: ProductPageAdditionalDataAction) =>
    action.data.reviews || state
});

const accessories = createReducer<RelatedProduct[] | undefined>([], {
  [PRODUCT_PAGE_ADDITIONAL_DATA_SET]: (_, action: ProductPageAdditionalDataAction) =>
    action.data.accessories || [],
  [PRODUCT_PAGE_VARIANT_DATA_SET]: (_, action: ProductPageVariantDataAction) =>
    action.data.accessories || []
});

const recommended = createReducer<ListViewProduct[]>([], {
  [PRODUCT_PAGE_ADDITIONAL_DATA_SET]: (_, action: ProductPageAdditionalDataAction) =>
    action.data.recommended || []
});

const bundleProductLinks = createReducer<BundleProductLink[]>([], {
  [PRODUCT_PAGE_ADDITIONAL_DATA_SET]: (_, action: ProductPageAdditionalDataAction) =>
    action.data.bundleProductLinks || []
});

const productsInSeries = createReducer<ListViewProduct[]>([], {
  [PRODUCT_PAGE_ADDITIONAL_DATA_SET]: (_, action: ProductPageAdditionalDataAction) =>
    action.data.productsInSeries || []
});

const showProductAdded = createReducer<boolean>(false, {
  [PRODUCT_PAGE_PRODUCT_ADDED_TO_CART]: () => true
});

const adminProperties = createReducer<IProductPageExtendedModel['adminProperties'] | undefined>(
  {},
  {
    [PRODUCT_PAGE_VARIANT_DATA_SET]: (state, action: ProductPageVariantDataAction) =>
      action.data.adminProperties || state
  }
);

const packageProduct = createReducer<Nullable<PackageProduct>>(
  {},
  {
    [PRODUCT_PAGE_ADDITIONAL_DATA_SET]: (state, action: ProductPageAdditionalDataAction) => {
      if (action.data.packageProduct !== undefined) {
        const partialProducts = state.products ? state.products : [];
        const fullProducts = action.data.packageProduct.products;
        return {
          ...state,
          products: fullProducts.map(p => {
            const partial = partialProducts.find(partial => partial.productId === p.productId);
            return {
              ...p,
              quantity: {
                ...p.quantity,
                amount: partial ? partial.quantity.amount : p.quantity.amount
              }
            };
          })
        };
      }
      return state;
    }
  }
);

const samples = createReducer<SampleProduct[]>([], {
  [PRODUCT_PAGE_VARIANT_DATA_SET]: (_, action: ProductPageAdditionalDataAction) => {
    return action.data.samples || [];
  }
});

const seriesFilter = createReducer<string | undefined>('', {
  [PRODUCT_PAGE_ADDITIONAL_DATA_SET]: (
    _: string,
    { data: { seriesFilter } }: ProductPageAdditionalDataAction
  ) => seriesFilter
});

const addonServices = createReducer<AddonServices | undefined>(
  { addonServiceProducts: [], addonServicesZipCode: '' },
  {
    [ADDON_SERVICE_SELECT]: (state, action: AddonServiceSelectAction) => {
      if (state === undefined) {
        return state;
      }

      const newAddonServiceProducts = selectProductById(
        state.addonServiceProducts,
        action.selectedId
      );

      return { ...state, addonServiceProducts: newAddonServiceProducts };
    },
    [ADDON_SERVICE_DESELECT]: (state, action: AddonServiceSelectAction) => {
      if (state === undefined) {
        return state;
      }

      const newAddonServiceProducts = deselectProductById(
        state.addonServiceProducts,
        action.selectedId
      );

      return { ...state, addonServiceProducts: newAddonServiceProducts };
    },
    [ADDON_SERVICE_DESELECT_ALL]: state => {
      if (state === undefined) {
        return state;
      }

      const newAddonServiceProducts = deselectAllProducts(state.addonServiceProducts);

      return { ...state, addonServiceProducts: newAddonServiceProducts };
    },
    [ADDON_SERVICE_ZIP_CODE_SET]: (state, action: AddonServiceZipCodeSetAction) => {
      if (state === undefined) {
        return state;
      }
      return {
        ...state,
        addonServicesZipCode: action.zipCode
      };
    },
    [UPDATE_CURRENT_PAGE]: state => {
      if (state === undefined) {
        return state;
      }

      return { ...state, addonServicesZipCode: '' };
    },
    [PRODUCT_PAGE_VARIANT_DATA_SET]: (state, action: ProductPageVariantDataAction) => {
      if (state === undefined) {
        return state;
      }

      const products = action.data.addonServices?.addonServiceProducts;

      return products ? { ...state, addonServiceProducts: products } : state;
    }
  }
);

const variantLoadingStatus = createReducer(initialState.variantLoadingStatus, {
  [PRODUCT_PAGE_VARIANT_LOADING_STATUS]: (_, { status }: ProductPageVariantLoadingStatusAction) =>
    status
});

// Reducers use null keys in the reducer to signal potential keys
export default combineReducers<ProductPageModel, ReducerOptionalMapObject>({
  ...initialState,
  leaderboardAd: null,
  brandLogo: null,
  topContentArea: null,
  offerLink: null,
  ...reviewReducers,
  samples,
  packageProduct,
  othersAlsoBoughtProducts,
  seriesFilter,
  productsInSeries,
  product,
  configurator,
  topSellers,
  addons,
  accessories,
  bundledProduct,
  reviews,
  showProductAdded,
  recommended,
  bundleProductLinks,
  adminProperties,
  video,
  addonServices,
  variantLoadingStatus,
  priceGuarantee: null
});
